import React, { useState } from "react";
import styled from "@emotion/styled";
import BasicButton from "@components/button/BasicButton";
import LabelWithInput from "@components/input/LabelWithInput";
import CustomSelectBox from "@components/input/CustomSelectBox";
import { useFormContext } from "react-hook-form";
import { Spin, Select } from "antd";
import { LoadingOutlined } from "@ant-design/icons";

type TagItem = {
  id: number;
  name: string;
};

type Props = {
  label: string;
  fieldName: string;
  aiTags: TagItem[];
  allTags: TagItem[];
  required?: boolean;
  loading?: boolean;
  maxSelection?: number;
};

const TagAttributeSelector = ({
  label,
  fieldName,
  aiTags,
  allTags,
  required = false,
  loading = false,
  maxSelection = 1,
}: Props) => {
  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();
  const [isCustomMode, setIsCustomMode] = useState(false);

  const selectedTags: number[] = watch(fieldName) || [];
  const fieldError = (errors as any)?.[fieldName]?.message as string | undefined;

  const toggleAiTag = (tagId: number) => {
    if (selectedTags.includes(tagId)) {
      setValue(fieldName, selectedTags.filter((id) => id !== tagId), { shouldValidate: true });
      return;
    }

    if (selectedTags.length >= maxSelection) {
      if (maxSelection === 1) {
        // 단일 선택: 교체
        setIsCustomMode(false);
        setValue(fieldName, [tagId], { shouldValidate: true });
      }
      return;
    }

    setIsCustomMode(false);
    setValue(fieldName, [...selectedTags, tagId], { shouldValidate: true });
  };

  const handleCustomMode = () => {
    setIsCustomMode(true);
    setValue(fieldName, [], { shouldValidate: true });
  };

  const handleAiMode = () => {
    setIsCustomMode(false);
    setValue(fieldName, [], { shouldValidate: true });
  };

  const handleDirectSelect = (value: any) => {
    if (maxSelection > 1) {
      // 멀티 선택: value는 배열
      const values = (Array.isArray(value) ? value : [value]).map(Number).filter(Boolean);
      setValue(fieldName, values.slice(0, maxSelection), { shouldValidate: true });
    } else {
      // 단일 선택
      const numericValue = Number(value);
      if (!numericValue) {
        setValue(fieldName, [], { shouldValidate: true });
        return;
      }
      setValue(fieldName, [numericValue], { shouldValidate: true });
    }
  };

  if (loading) {
    return (
      <div>
        <LabelWithInput label={label} required={required} />
        <div style={{ paddingTop: "0.8rem" }} />
        <LoadingContainer>
          <Spin indicator={<LoadingOutlined spin style={{ fontSize: 20 }} />} />
          <LoadingText>AI 분석 중...</LoadingText>
        </LoadingContainer>
      </div>
    );
  }

  return (
    <div>
      <LabelWithInput label={label} required={required} />
      <div style={{ paddingTop: "0.8rem" }} />

      {!isCustomMode ? (
        <Container>
          {aiTags.map((tag) => (
            <BasicButton
              key={tag.id}
              title={tag.name}
              fit
              gray
              active={selectedTags.includes(tag.id)}
              width="100%"
              fontSize="1.2rem"
              transparent
              padding="1rem"
              borderColor="#ccc"
              onClick={() => toggleAiTag(tag.id)}
            />
          ))}
          <BasicButton
            title="직접 선택"
            fit
            gray
            active={isCustomMode}
            width="100%"
            fontSize="1.2rem"
            transparent
            padding="1rem"
            borderColor="#ccc"
            onClick={handleCustomMode}
          />
          {aiTags.length === 0 && <HelperText>AI 추천 결과가 없습니다.</HelperText>}
        </Container>
      ) : (
        <CustomContainer>
          <ButtonRow>
            <BasicButton
              title="AI 추천으로 돌아가기"
              fit
              gray
              width="100%"
              fontSize="1.2rem"
              transparent
              padding="1rem"
              borderColor="#ccc"
              onClick={handleAiMode}
            />
          </ButtonRow>
          <CustomLabel>직접 선택{maxSelection > 1 ? ` (최대 ${maxSelection}개)` : ""}</CustomLabel>
          {maxSelection > 1 ? (
            <StyledMultiSelect
              mode="multiple"
              maxCount={maxSelection}
              value={selectedTags}
              options={allTags.map((tag) => ({
                label: tag.name,
                value: tag.id,
              }))}
              placeholder="태그를 선택해주세요."
              onChange={handleDirectSelect}
            />
          ) : (
            <CustomSelectBox
              value={selectedTags[0] ?? undefined}
              options={allTags.map((tag) => ({
                label: tag.name,
                value: tag.id,
              }))}
              placeholder="태그를 선택해주세요."
              onChange={handleDirectSelect}
            />
          )}
        </CustomContainer>
      )}

      {fieldError && <ErrorMessage>{fieldError}</ErrorMessage>}
    </div>
  );
};

export default TagAttributeSelector;

const Container = styled.div({
  display: "flex",
  flexWrap: "wrap",
  gap: "1rem",
  justifyContent: "flex-start",
  alignItems: "center",
});

const CustomContainer = styled.div({
  display: "flex",
  flexDirection: "column",
  border: "1px solid #ccc",
  width: "100%",
  padding: "1rem",
  gap: "1rem",
  marginTop: "1rem",
});

const ButtonRow = styled.div({
  display: "flex",
  gap: "1rem",
});

const CustomLabel = styled.div({
  fontSize: "1.2rem",
  fontWeight: 600,
  color: "#666",
});

const LoadingContainer = styled.div({
  display: "flex",
  alignItems: "center",
  gap: "0.8rem",
  padding: "1rem",
});

const LoadingText = styled.div(({ theme }) => ({
  fontSize: "1.2rem",
  color: theme.color.gray,
}));

const ErrorMessage = styled.div(({ theme }) => ({
  color: theme.color.orange,
  fontSize: "1.2rem",
  marginTop: "0.8rem",
}));

const HelperText = styled.div(({ theme }) => ({
  color: theme.color.gray,
  fontSize: "1.2rem",
}));

const StyledMultiSelect = styled(Select)({
  width: "100%",
  ".ant-select-selector": {
    minHeight: "5.2rem !important",
    borderRadius: "0rem",
    fontSize: "1.6rem",
    padding: "0.4rem 1.6rem !important",
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
  },
});
